import React, { useEffect } from 'react';
import Chart from 'chart.js/auto';

interface IPieChart {
  chartData: {
    labels: string[];
    datasets: {
      label?: string;
      data: number[];
      backgroundColor?: string[]; // optional colors
    }[];
  };
  uuid: string;
  title?: string;
  height?: number;
  width?: number;
  showLabels?: boolean; // Option to show/hide labels on pie slices
}

// Plugin to draw labels on pie slices
const drawSliceLabels = {
  id: 'drawSliceLabels',
  afterDraw: (chart: any) => {
    const ctx = chart.ctx;
    const meta = chart.getDatasetMeta(0);
    
    ctx.save();
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    meta.data.forEach((arc: any, index: number) => {
      const value = chart.data.datasets[0].data[index] as number;
      const label = chart.data.labels?.[index] as string;
      
      // Only show label if slice is large enough and value is greater than 0
      if (value > 0) {
        const angle = (arc.startAngle + arc.endAngle) / 2;
        const x = arc.x + Math.cos(angle) * (arc.innerRadius + (arc.outerRadius - arc.innerRadius) / 2);
        const y = arc.y + Math.sin(angle) * (arc.innerRadius + (arc.outerRadius - arc.innerRadius) / 2);
        
        // Draw label with background for better visibility
        const text = `${label}\n${value}`;
        const lines = text.split('\n');
        const lineHeight = 14;
        const padding = 4;
        const textWidth = Math.max(...lines.map((line: string) => ctx.measureText(line).width));
        
        // Draw background rectangle with rounded corners
        const bgX = x - textWidth / 2 - padding;
        const bgY = y - (lines.length * lineHeight) / 2 - padding;
        const bgWidth = textWidth + padding * 2;
        const bgHeight = lines.length * lineHeight + padding * 2;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.lineWidth = 1;
        
        // Draw rounded rectangle
        const radius = 4;
        ctx.beginPath();
        ctx.moveTo(bgX + radius, bgY);
        ctx.lineTo(bgX + bgWidth - radius, bgY);
        ctx.quadraticCurveTo(bgX + bgWidth, bgY, bgX + bgWidth, bgY + radius);
        ctx.lineTo(bgX + bgWidth, bgY + bgHeight - radius);
        ctx.quadraticCurveTo(bgX + bgWidth, bgY + bgHeight, bgX + bgWidth - radius, bgY + bgHeight);
        ctx.lineTo(bgX + radius, bgY + bgHeight);
        ctx.quadraticCurveTo(bgX, bgY + bgHeight, bgX, bgY + bgHeight - radius);
        ctx.lineTo(bgX, bgY + radius);
        ctx.quadraticCurveTo(bgX, bgY, bgX + radius, bgY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Draw text
        ctx.fillStyle = '#333';
        lines.forEach((line: string, i: number) => {
          ctx.fillText(line, x, y - (lines.length - 1) * lineHeight / 2 + i * lineHeight);
        });
      }
    });
    
    ctx.restore();
  },
};

const PieChart: React.FC<IPieChart> = (props) => {
  const id = props.uuid || 'pie';

  useEffect(() => {
    const ctx = document.getElementById(id) as HTMLCanvasElement;
    if (!ctx) return;

    const chartInstance = new Chart(ctx, {
      type: 'pie',
      data: props.chartData,
      plugins: props.showLabels !== false ? [drawSliceLabels] : [],
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              generateLabels: (chart) => {
                const data = chart.data;
                const dataset = data.datasets[0] as any;

                return data.labels!.map((label: any, index) => {
                  const value = dataset.data[index] as number;
                  const color = dataset.backgroundColor?.[index] || 'gray';

                  return {
                    text: `${label}: ${value}`,
                    fillStyle: color,
                    strokeStyle: color,
                    lineWidth: 1,
                    hidden: false,
                    index,
                  };
                });
              },
            },
          },
          title: {
            display: !!props.title,
            text: props.title,
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
      },
    });

    return () => {
      chartInstance.destroy();
    };
  }, [props]);

  return (
    <div>
      <canvas id={id} width={props.width || 300} height={props.height || 300} />
    </div>
  );
};

export default PieChart;
